/**
 * RecipeEmojiModal — modal for changing a recipe's key-visual emoji from the
 * library page.
 *
 * Chrome cloned from RecipeRenameModal (Instrument): --surface panel,
 * --line-strong hairline border, no shadow beyond the permitted CTA one,
 * full-width bottom-sheet < 640px / centered panel ≥ 640px, Esc + overlay
 * close, focus trap on the panel, inline error.
 *
 * Body = the lazy searchable emoji picker (EmojiPickerPanel — code-split via
 * React.lazy so frimousse + the Emojibase dataset are not in the initial app
 * bundle) + a "Reset to default" control that clears the emoji (onChange(null)
 * → card renders the 🍳 fallback).
 *
 * Rule 8: on persist failure the modal stays OPEN and shows an inline
 * brand-voice error; the failure is never silently swallowed.
 *
 * Design refs:
 *   design.md §2 — one accent (selected emoji preview, focus ring); hairlines.
 *   design.md §4 — tokens.
 *   design.md §15.3 — 44px min tap target.
 *   branding.md §5.2.1 — no banned words, no exclamations.
 *   branding.md §8 — "Recipe" not "Repository".
 *
 * CLAUDE.md Rule 7 — mobile + tablet + desktop from first commit.
 */

import { useState, useEffect, useRef, lazy, Suspense } from "react";

// Code-split: frimousse + the lazily-fetched Emojibase dataset stay out of the
// initial bundle and load only when this modal mounts. Precedent:
// entry.client.tsx + Workspace lazy mount in routes/app.tsx.
const EmojiPickerPanel = lazy(() => import("./EmojiPickerPanel"));

const FALLBACK_EMOJI = "🍳";

// ─── Styles (mirror RecipeRenameModal so the look-and-feel matches) ───────────

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "var(--scrim)",
  display: "flex",
  zIndex: 1000,
};

const SHEET_CSS = `
  @media (max-width: 639px) {
    .proof-recipe-emoji-overlay {
      align-items: flex-end !important;
      justify-content: stretch !important;
    }
    .proof-recipe-emoji-panel {
      border-radius: var(--radius-sheet-top) var(--radius-sheet-top) 0 0 !important;
      max-width: 100% !important;
      width: 100% !important;
    }
  }
  @media (min-width: 640px) {
    .proof-recipe-emoji-overlay {
      align-items: center;
      justify-content: center;
    }
    .proof-recipe-emoji-panel {
      width: 360px;
    }
  }
`;

const panelStyle: React.CSSProperties = {
  background: "var(--surface)",
  borderRadius: "var(--radius-card)",
  // --line-strong (not --line): readable panel edge on --bg.
  border: "1px solid var(--line-strong)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const headerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "var(--space-4) var(--space-4) var(--space-3)",
  borderBottom: "1px solid var(--line)",
  flexShrink: 0,
};

const titleStyle: React.CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontSize: "14px",
  fontWeight: 500,
  color: "var(--text)",
};

const closeButtonStyle: React.CSSProperties = {
  background: "none",
  border: "none",
  cursor: "pointer",
  color: "var(--muted)",
  fontSize: "18px",
  lineHeight: 1,
  padding: "var(--space-1)",
  borderRadius: "4px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "color var(--motion-state)",
};

const bodyStyle: React.CSSProperties = {
  padding: "var(--space-4)",
  display: "flex",
  flexDirection: "column",
  gap: "var(--space-3)",
};

const currentRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "var(--space-3)",
};

const currentTileStyle: React.CSSProperties = {
  flexShrink: 0,
  width: "40px",
  height: "40px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "8px",
  background: "var(--bg)",
  border: "1px solid var(--line)",
  fontFamily: "'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji', sans-serif",
  fontSize: "22px",
  lineHeight: 1,
  userSelect: "none",
};

const currentLabelStyle: React.CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontSize: "12px",
  color: "var(--muted)",
};

const pickerFallbackStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "320px",
  fontFamily: "var(--font-sans)",
  fontSize: "13px",
  color: "var(--muted)",
};

const errorStyle: React.CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontSize: "12px",
  color: "var(--del)",
};

const footerStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "row",
  gap: "var(--space-2)",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "var(--space-3) var(--space-4)",
  borderTop: "1px solid var(--line)",
  flexShrink: 0,
};

function resetButtonStyle(disabled: boolean): React.CSSProperties {
  return {
    background: "none",
    border: "1px solid var(--line)",
    borderRadius: "var(--radius-button)",
    padding: "var(--space-2) var(--space-3)",
    fontFamily: "var(--font-sans)",
    fontSize: "13px",
    color: "var(--muted)",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    minHeight: "var(--tap-target-min)",
    transition: "color var(--motion-state), border-color var(--motion-state)",
  };
}

const cancelButtonStyle: React.CSSProperties = {
  background: "none",
  border: "1px solid var(--line)",
  borderRadius: "var(--radius-button)",
  padding: "var(--space-2) var(--space-4)",
  fontFamily: "var(--font-sans)",
  fontSize: "13px",
  color: "var(--muted)",
  cursor: "pointer",
  minHeight: "var(--tap-target-min)",
  transition: "color var(--motion-state), border-color var(--motion-state)",
};

// ─── Component ────────────────────────────────────────────────────────────────

export interface RecipeEmojiModalProps {
  /** UUID of the recipe whose emoji is being changed. */
  recipeUuid: string;
  /** Human title — shown in the header so the user knows which recipe. */
  recipeTitle: string;
  /** Current emoji, or undefined when none is set (shows the 🍳 fallback). */
  currentEmoji?: string;
  /**
   * Persist handler. Pass a string to set, or `null` to reset to the default.
   * Must reject on failure so the modal keeps the inline error visible and
   * stays mounted (Rule 8). The modal calls onClose itself on resolve.
   */
  onChange: (emoji: string | null) => Promise<void>;
  onClose: () => void;
}

export function RecipeEmojiModal({
  recipeUuid: _recipeUuid,
  recipeTitle,
  currentEmoji,
  onChange,
  onClose,
}: RecipeEmojiModalProps) {
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Focus the panel on mount so Esc + Tab are scoped to the dialog (the picker
  // search input takes focus on first Tab; the panel container is the focus
  // anchor that keeps the trap simple without a third-party lib).
  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  useEffect(() => {
    function onKeyDown(e: globalThis.KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  function handleOverlayClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === overlayRef.current) onClose();
  }

  async function persist(next: string | null) {
    setSubmitting(true);
    setError(null);
    try {
      await onChange(next);
      onClose();
    } catch (err: unknown) {
      console.error({ event: "recipe_emoji_change_failed", err });
      // branding.md §5.3 — precise, no exclamation, actionable.
      setError("Couldn't change the emoji. Try again.");
      setSubmitting(false);
    }
  }

  function handleSelect(emoji: string) {
    if (submitting) return;
    void persist(emoji);
  }

  function handleReset() {
    if (submitting) return;
    void persist(null);
  }

  const displayEmoji = currentEmoji && currentEmoji.length > 0 ? currentEmoji : FALLBACK_EMOJI;
  const canReset = !submitting && currentEmoji !== undefined && currentEmoji.length > 0;

  return (
    <>
      <style>{SHEET_CSS}</style>
      <div
        ref={overlayRef}
        className="proof-recipe-emoji-overlay"
        style={overlayStyle}
        onClick={handleOverlayClick}
        data-testid="recipe-emoji-modal"
      >
        <div
          ref={panelRef}
          className="proof-recipe-emoji-panel"
          style={panelStyle}
          role="dialog"
          aria-modal="true"
          aria-label="Change recipe emoji"
          tabIndex={-1}
        >
          <div style={headerStyle}>
            <span style={titleStyle}>Change emoji</span>
            <button type="button" style={closeButtonStyle} aria-label="Close" onClick={onClose}>
              ×
            </button>
          </div>

          <div style={bodyStyle}>
            <div style={currentRowStyle}>
              <span style={currentTileStyle} aria-hidden="true" data-testid="recipe-emoji-current">
                {displayEmoji}
              </span>
              <span style={currentLabelStyle}>{recipeTitle}</span>
            </div>

            <Suspense
              fallback={
                <div style={pickerFallbackStyle} data-testid="recipe-emoji-loading">
                  Loading emoji…
                </div>
              }
            >
              <EmojiPickerPanel onSelect={handleSelect} />
            </Suspense>

            {error && (
              <span style={errorStyle} role="alert" data-testid="recipe-emoji-error">
                {error}
              </span>
            )}
          </div>

          <div style={footerStyle}>
            <button
              type="button"
              style={resetButtonStyle(!canReset)}
              onClick={handleReset}
              disabled={!canReset}
              data-testid="recipe-emoji-reset"
            >
              Reset to default
            </button>
            <button
              type="button"
              style={cancelButtonStyle}
              onClick={onClose}
              disabled={submitting}
              data-testid="recipe-emoji-cancel"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
